package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.data.repository.UniliteralRepository
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.looksLikeGardinerCode
import com.shadowings.gero.presentation.model.HeartActions
import com.shadowings.gero.presentation.model.QuizUiState
import com.shadowings.gero.presentation.screen.QuizScreenContent
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.ui.quizSelectionCorrectBg
import com.shadowings.gero.presentation.ui.quizSelectionPendingBg
import com.shadowings.gero.presentation.ui.quizSelectionWrongBg
import com.shadowings.gero.presentation.ui.testModeCorrect
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.quiz_show_options_button
import gero.composeapp.generated.resources.share_quiz_hiero
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Preview(name = "Small Screen (Low Height)", heightDp = 480, widthDp = 320, showBackground = true)
@Preview(name = "Landscape", widthDp = 800, heightDp = 360, showBackground = true)
@Composable
private fun PhoneticSingleSelectionComposablePreview() {
    AppTheme {
        Surface {
            AppTheme {
                Surface {
                    QuizScreenContent(
                        uiState = QuizUiState(
                            lessonId = LessonIds.INTRO_HIEROGLYPHS,
                            quizList = listOf(
                                SingleSelectionContent(
                                    text = QuizText.Plain("Come si pronuncia questo geroglifico?"),
                                    detail = "D21",
                                    options = listOf(
                                        Option("𓂀 Occhio di Ra", correct = true),
                                        Option("ABC", correct = false),
                                        Option(
                                            "lorem ipsum dolor sit amet mega lungher",
                                            correct = false
                                        ),
                                        Option("123", correct = false),
                                    )
                                )
                            ),
                            userData = UserData()
                        ),
                        hiero = listOf(),
                        onQuizCompleted = { _, _, _ -> },
                        onAddExperience = {},
                        heartActions = HeartActions({}),
                        navController = rememberNavController()
                    )
                }
            }
        }
    }
}

/**
 * Copiato da [MultiSelectionComposable].
 * Unica differenza: qui solo un'opzione alla volta può essere selezionata.
 */
@Composable
fun PhoneticSingleSelectionComposable(
    quiz: SingleSelectionContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    testMode: Boolean,
    userData: UserData,
    lessonId: String = "",
    hideOptions: Boolean = false,
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null
) {
    val deviceInfo = rememberDeviceInfo()
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val graphicsLayer = rememberGraphicsLayer()
    val hapticFeedback = LocalHapticFeedback.current

    var selections by rememberSaveable { mutableStateOf(BooleanArray(quiz.options.size)) }
    var revealed by rememberSaveable(quiz.id) { mutableStateOf(false) }

    val isLandscape = deviceInfo.isLandscape

    Box(modifier = Modifier.fillMaxSize()) {
        run {
            val questionContent = @Composable {
                val uniliteralRes = UniliteralRepository.resFromGardinerCode(quiz.detail)
                val wordContent: (@Composable () -> Unit)? = when {
                    uniliteralRes != null -> {
                        {
                            Image(
                                modifier = Modifier.fillMaxWidth(0.3f).aspectRatio(1f),
                                painter = painterResource(uniliteralRes),
                                contentDescription = quiz.detail,
                                colorFilter = ColorFilter.tint(hieroglyphTint)
                            )
                        }
                    }

                    quiz.detail.isNotBlank() -> {
                        {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .drawWithContent {
                                        graphicsLayer.record {
                                            this@drawWithContent.drawContent()
                                        }
                                        drawContent()
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                MdCWord(
                                    word = quiz.detail,
                                    rtl = false,
                                    cart = false,
                                    size = 96.dp,
                                    testTag = quiz.detail
                                )
                            }
                        }
                    }

                    else -> null
                }
                QuestionBox(
                    question = quiz.text.resolve(),
                    questionTestTag = "multi_selection_question",
                    wordContent = wordContent
                )
            }

            val optionsContent = @Composable {
                if (hideOptions && !revealed) {
                    RevealOptionsButton(onClick = { revealed = true })
                } else {
                quiz.options.forEachIndexed { index, option ->
                    val optionText = option.text.resolve()
                    val selected = selections[index]
                    val bg = when {
                        state.blinking -> MaterialTheme.colorScheme.surfaceVariant
                        selected && !state.showResult -> quizSelectionPendingBg
                        state.showResult && option.correct -> quizSelectionCorrectBg
                        selected && state.showResult && !option.correct -> quizSelectionWrongBg
                        else -> MaterialTheme.colorScheme.surfaceVariant
                    }

                    val isGardiner = looksLikeGardinerCode(optionText)

                    if (isGardiner) {
                        OutlinedCard(
                            onClick = {
                                if (state.pending) {
                                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                    val toggled = !selections[index]
                                    selections = BooleanArray(quiz.options.size) { i ->
                                        if (i == index) toggled else false
                                    }
                                }
                            },
                            enabled = !state.showResult,
                            shape = RoundedCornerShape(12.dp),
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = bg,
                                contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                                disabledContainerColor = bg,
                                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
                            ),
                            border = BorderStroke(
                                width = if (selected && !state.showResult) 2.dp else 1.dp,
                                color = if (selected && !state.showResult) {
                                    MaterialTheme.colorScheme.primary
                                } else if (testMode && option.correct) {
                                    testModeCorrect
                                } else {
                                    MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                                }
                            ),
                            modifier = Modifier
                                .fillMaxWidth()
                                .semantics { testTag = optionText }
                        ) {
                            Box(
                                modifier = Modifier.fillMaxWidth()
                                    .heightIn(min = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) 48.dp else 64.dp)
                                    .padding(vertical = 8.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                MdCWord(
                                    word = optionText,
                                    rtl = false,
                                    cart = false,
                                    size = 60.dp,
                                    testTag = optionText
                                )
                            }
                        }
                    } else {
                        OutlinedCard(
                            onClick = {
                                if (state.pending) {
                                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                    val toggled = !selections[index]
                                    selections = BooleanArray(quiz.options.size) { i ->
                                        if (i == index) toggled else false
                                    }
                                }
                            },
                            enabled = !state.showResult,
                            shape = RoundedCornerShape(12.dp),
                            colors = CardDefaults.outlinedCardColors(
                                containerColor = bg,
                                contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                                disabledContainerColor = bg,
                                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
                            ),
                            border = BorderStroke(
                                width = if (selected && !state.showResult) 2.dp else 1.dp,
                                color = if (selected && !state.showResult) {
                                    MaterialTheme.colorScheme.primary
                                } else if (testMode && option.correct) {
                                    testModeCorrect
                                } else {
                                    MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                                }
                            ),
                            modifier = Modifier
                                .fillMaxWidth()
                                .semantics { testTag = optionText }
                        ) {
                            Box(
                                modifier = Modifier.fillMaxWidth()
                                    .heightIn(min = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) 48.dp else 64.dp)
                                    .padding(horizontal = 8.dp, vertical = 8.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = optionText.lowercase(),
                                    style = MaterialTheme.typography.bodyLarge,
                                    fontWeight = FontWeight.Bold,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                }
                }
            }

            QuizContentScaffold(
                testTag = "true_false_content",
                isLandscape = isLandscape,
                questionContent = { questionContent() },
                optionsContent = { optionsContent() }
            )
        }
        val correctIndices = quiz.options.mapIndexedNotNull { index, option ->
            if (option.correct) index else null
        }
        val correctOptionText = quiz.options.firstOrNull { it.correct }?.text?.resolve()
        val isCorrectGardiner = correctOptionText != null && looksLikeGardinerCode(correctOptionText)
        // A single-selection quiz should only ever have one correct option; the multi-correct
        // branch below is a defensive fallback (no visual hieroglyph to point at, so it falls
        // back to naming the option's position instead).
        val displayCorrectAnswer: String?
        val correctAnswerIsHieroglyph: Boolean
        if (correctIndices.size > 1) {
            val ordinals = listOf("la prima", "la seconda", "la terza", "la quarta", "la quinta")
            val names = correctIndices.map { ordinals.getOrElse(it) { "la ${it + 1}ª" } }
            displayCorrectAnswer = "erano " + names.dropLast(1).joinToString(", ") + " e " + names.last()
            correctAnswerIsHieroglyph = false
        } else {
            displayCorrectAnswer = correctOptionText
            correctAnswerIsHieroglyph = isCorrectGardiner
        }

        val resolvedInfo = quiz.info?.resolve()
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: Single Selection Quiz
            Question: ${quiz.text.resolve()}
            Detail: ${quiz.detail}
            Options:
            ${
            quiz.options.map { "${it.text.resolve()} (Correct: ${it.correct})" }
                .joinToString("\n            ")
        }
        """.trimIndent()
        val userAnswer = quiz.options.mapIndexedNotNull { index, option ->
            if (selections.getOrElse(index) { false }) option.text.resolve() else null
        }.firstOrNull() ?: "None"
        val resolvedQuestion = quiz.text.resolve()
        val resolvedOptions = quiz.options.map { it.text.resolve() }

        val shareText = stringResource(Res.string.share_quiz_hiero)

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = selections.any { it },
            info = resolvedInfo,
            correctAnswer = displayCorrectAnswer,
            correctAnswerIsHieroglyph = correctAnswerIsHieroglyph,
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = shareText,
            shareImageProvider = {
                try {
                    graphicsLayer.toImageBitmap()
                } catch (e: Exception) {
                    null
                }
            },
            onConfirmClick = {
                val isCorrect = selections.mapIndexed { index, bool ->
                    bool == quiz.options[index].correct
                }.all { it }
                onAnswerResult?.invoke(
                    isCorrect,
                    WrongAnswerRecord(
                        id = WrongAnswerRecord.buildId(
                            lessonId,
                            quiz.detail.ifBlank { resolvedQuestion }
                        ),
                        lessonId = lessonId,
                        type = QuizQuestionType.SINGLE_SELECTION,
                        questionText = resolvedQuestion,
                        correctAnswerText = displayCorrectAnswer ?: "",
                        userAnswerText = userAnswer,
                        hieroglyphs = quiz.detail,
                        answerIsHieroglyph = correctAnswerIsHieroglyph,
                        options = resolvedOptions
                    )
                )
                isCorrect
            }
        )
    }
}

@Composable
private fun RevealOptionsButton(onClick: () -> Unit) {
    val hapticFeedback = LocalHapticFeedback.current
    Box(
        modifier = Modifier.fillMaxWidth().padding(vertical = 24.dp),
        contentAlignment = Alignment.Center
    ) {
        Button(
            onClick = {
                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                onClick()
            }
        ) {
            Text(text = stringResource(Res.string.quiz_show_options_button))
        }
    }
}
